import { fromJS } from "immutable";
import ACTIONS from "../constants";

const initialState = fromJS({
  messages: [],
  isAssistantTyping: false,
  isStreaming: false,
  streamingMessageId: null,
  isLoadingOlder: false,
  hasMoreOlder: false,
  nextBeforeId: null,
  messagesPageSize: null,
  error: null,
  conversationId: null,
  activeFilters: null,
  contextSummary: null,
});

const toMessageList = (messages) => {
  if (!messages) return [];
  return typeof messages.toJS === "function" ? messages.toJS() : messages;
};

export default function vertex360CopilotReducer(state = initialState, action) {
  switch (action.type) {
    case ACTIONS.VERTEX360_COPILOT_APPEND_MESSAGE: {
      const messages = toMessageList(state.get("messages"));
      return state.set("messages", fromJS([...messages, action.payload]));
    }

    case ACTIONS.VERTEX360_COPILOT_PREPEND_MESSAGES: {
      const messages = toMessageList(state.get("messages"));
      const older = Array.isArray(action.payload) ? action.payload : [];
      return state.set("messages", fromJS([...older, ...messages]));
    }

    case ACTIONS.VERTEX360_COPILOT_UPDATE_MESSAGE_CONTENT: {
      const { id, content, isError } = action.payload || {};
      const targetId = String(id);
      const messages = toMessageList(state.get("messages")).map((message) => {
        if (String(message.id) !== targetId) return message;
        const next = { ...message, content };
        if (isError !== undefined) {
          next.isError = isError;
        }
        return next;
      });
      return state.set("messages", fromJS(messages));
    }

    case ACTIONS.VERTEX360_COPILOT_REMOVE_MESSAGE: {
      const targetId = String(action.payload?.id ?? "");
      if (!targetId) return state;
      const messages = toMessageList(state.get("messages")).filter(
        (message) => String(message.id) !== targetId
      );
      const streamingMessageId = state.get("streamingMessageId");
      const nextStreamingId =
        streamingMessageId != null && String(streamingMessageId) === targetId
          ? null
          : streamingMessageId;
      return state
        .set("messages", fromJS(messages))
        .set("streamingMessageId", nextStreamingId ?? null);
    }

    case ACTIONS.VERTEX360_COPILOT_SET_MESSAGE_RENDER: {
      const {
        id,
        render,
        sources,
        suggestions,
        isReducedMode,
        degradeReason,
        servicePath,
      } = action.payload || {};
      const targetId = String(id);
      const messages = toMessageList(state.get("messages")).map((message) => {
        if (String(message.id) !== targetId) return message;
        return {
          ...message,
          ...(render !== undefined ? { render } : {}),
          ...(sources !== undefined ? { sources } : {}),
          ...(suggestions !== undefined ? { suggestions } : {}),
          ...(isReducedMode !== undefined ? { isReducedMode } : {}),
          ...(degradeReason !== undefined ? { degradeReason } : {}),
          ...(servicePath !== undefined ? { servicePath } : {}),
        };
      });
      return state.set("messages", fromJS(messages));
    }

    case ACTIONS.VERTEX360_COPILOT_SET_MESSAGE_FEEDBACK: {
      const { id, feedbackRating } = action.payload || {};
      const targetId = String(id ?? "");
      const rating =
        feedbackRating === "up" || feedbackRating === "down"
          ? feedbackRating
          : null;
      if (!targetId || !rating) return state;
      const messages = toMessageList(state.get("messages")).map((message) =>
        String(message.id) === targetId
          ? { ...message, feedbackRating: rating }
          : message
      );
      return state.set("messages", fromJS(messages));
    }

    case ACTIONS.VERTEX360_COPILOT_REKEY_MESSAGE: {
      const fromId = String(action.payload?.fromId ?? "");
      const toId = String(action.payload?.toId ?? "");
      if (!fromId || !toId || fromId === toId) return state;

      const messages = toMessageList(state.get("messages")).map((message) =>
        String(message.id) === fromId ? { ...message, id: toId } : message
      );

      const streamingMessageId = state.get("streamingMessageId");
      const nextStreamingId =
        streamingMessageId != null && String(streamingMessageId) === fromId
          ? toId
          : streamingMessageId;

      return state
        .set("messages", fromJS(messages))
        .set("streamingMessageId", nextStreamingId ?? null);
    }

    case ACTIONS.VERTEX360_COPILOT_SET_TYPING:
      return state.set(
        "isAssistantTyping",
        Boolean(action.payload?.isAssistantTyping)
      );

    case ACTIONS.VERTEX360_COPILOT_SET_STREAMING:
      return state
        .set("isStreaming", Boolean(action.payload?.isStreaming))
        .set("streamingMessageId", action.payload?.streamingMessageId ?? null);

    case ACTIONS.VERTEX360_COPILOT_SET_OLDER_PAGINATION: {
      let next = state;
      if (action.payload?.hasMoreOlder !== undefined) {
        next = next.set("hasMoreOlder", Boolean(action.payload.hasMoreOlder));
      }
      if (action.payload?.nextBeforeId !== undefined) {
        next = next.set("nextBeforeId", action.payload.nextBeforeId ?? null);
      }
      return next;
    }

    case ACTIONS.VERTEX360_COPILOT_SET_MESSAGES_PAGE_SIZE:
      return state.set(
        "messagesPageSize",
        Number(action.payload?.pageSize) > 0
          ? Number(action.payload.pageSize)
          : null
      );

    case ACTIONS.VERTEX360_COPILOT_SET_CONVERSATION_ID:
      return state.set("conversationId", action.payload?.conversationId ?? null);

    case ACTIONS.VERTEX360_COPILOT_SET_ACTIVE_FILTERS: {
      const filters = action.payload?.activeFilters;
      return state
        .set(
          "activeFilters",
          filters && typeof filters === "object" ? fromJS(filters) : null
        )
        .set("contextSummary", action.payload?.contextSummary ?? null);
    }

    case ACTIONS.VERTEX360_COPILOT_LOAD_OLDER_MESSAGES.PENDING:
      return state.set("isLoadingOlder", true).set("error", null);

    case ACTIONS.VERTEX360_COPILOT_LOAD_OLDER_MESSAGES.SUCCESS:
      return state.set("isLoadingOlder", false).set("error", null);

    case ACTIONS.VERTEX360_COPILOT_LOAD_OLDER_MESSAGES.ERROR:
      return state.set("isLoadingOlder", false).set("error", action.error);

    case ACTIONS.VERTEX360_COPILOT_SEND_MESSAGE.PENDING:
      return state.set("error", null);

    case ACTIONS.VERTEX360_COPILOT_SEND_MESSAGE.SUCCESS:
      return state
        .set("isAssistantTyping", false)
        .set("isStreaming", false)
        .set("streamingMessageId", null)
        .set("error", null);

    case ACTIONS.VERTEX360_COPILOT_SEND_MESSAGE.ERROR:
      return state
        .set("isAssistantTyping", false)
        .set("isStreaming", false)
        .set("streamingMessageId", null)
        .set("error", action.error);

    case ACTIONS.VERTEX360_COPILOT_STOP_GENERATION:
      return state
        .set("isAssistantTyping", false)
        .set("isStreaming", false)
        .set("streamingMessageId", null);

    case ACTIONS.VERTEX360_COPILOT_RESET_CONVERSATION:
      return initialState;

    default:
      return state;
  }
}
